import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import { useTranslation } from "react-i18next";
import { RootStoreContext } from "src/stores/RootStore";
import { observer } from "mobx-react";
import { UI } from "@wwimmo/ui";
import { useSelectionListKeyHandler } from "src/hooks/ticket/selection-list-key-handler/useSelectionListKeyHandler";
import { boldenMatchedSubstring } from "src/utils/Common";
import { IVatData } from "src/stores/krediflow/InvoiceTypes";
import { ErpType } from "src/network/User";
import { LOADING_VAT_DATA_STATE } from "src/stores/krediflow/AccountingVatStore";
import styles from "./VatCodeField.module.css";

interface IVatCodeFieldProps {
    accountingRowIndex: number;
}

const VatCodeFieldBase = (props: IVatCodeFieldProps) => {
    const { t } = useTranslation();
    const { accountingVatStore, invoiceStore, authStore } = useContext(RootStoreContext);

    const { accountingRowIndex } = props;

    const [displayVatCodeList, setDisplayVatCodeList] = useState<boolean>(false);
    const [lastScrollPositionYVatCodeItems, setLastSrollYPositionVatCodeItems] = useState<number>(0);

    const vatCodeInputRef = useRef<HTMLInputElement>(null);

    const resetSearchQuery = useCallback(() => {
        const currentSelectedVatCodeQueryString = accountingVatStore.getSelectedVatCodeQueryString(accountingRowIndex);

        accountingVatStore.setCurrentVatCodeSearchQuery(currentSelectedVatCodeQueryString ?? "", accountingRowIndex);
    }, [accountingRowIndex, accountingVatStore]);

    useEffect(() => {
        const handleClickOutsideVatCodeInput = (event: any) => {
            const target = event.target as HTMLElement;

            const isClickOutsideVatCodeInputOrList = () => {
                if (!displayVatCodeList || !vatCodeInputRef.current) {
                    return true;
                }

                const clickedInsideInput = vatCodeInputRef.current.contains(target);

                const clickedListItem =
                    target.classList.contains("list-item") && target.classList.contains("accounting-vatcode");

                const clickedIcon = target.localName === "path" || target.localName === "svg";

                const clickedHighlightedText =
                    target.classList.contains("highlighted-text") && target.classList.contains("accounting-vatcode");

                const clickedListItemChildElement =
                    target.parentElement?.classList.contains("list-item") &&
                    target.parentElement.classList.contains("accounting-vatcode");

                return !(
                    clickedInsideInput ||
                    clickedListItem ||
                    clickedListItemChildElement ||
                    clickedIcon ||
                    clickedHighlightedText
                );
            };

            if (isClickOutsideVatCodeInputOrList()) {
                setDisplayVatCodeList(false);

                if (
                    invoiceStore.currentInvoice?.isAccountingVatCodeSelected(accountingRowIndex) &&
                    accountingVatStore.getCurrentVatCodeSearchQuery(accountingRowIndex) === ""
                ) {
                    invoiceStore.resetAccountingVatCodeFormFields(accountingRowIndex);
                } else {
                    resetSearchQuery();
                }
            }
        };

        document.addEventListener("mousedown", handleClickOutsideVatCodeInput);

        return () => {
            document.removeEventListener("mousedown", handleClickOutsideVatCodeInput);
        };
    }, [
        displayVatCodeList,
        resetSearchQuery,
        accountingVatStore,
        invoiceStore.currentInvoice?.isAccountingVatCodeSelected,
        accountingRowIndex,
        invoiceStore
    ]);

    const updateAccountingWithSelectedVatCode = useCallback(
        async (vatData: IVatData) => {
            invoiceStore.updateAccountingWithSelectedVatCode(vatData, accountingRowIndex);
        },
        [invoiceStore, accountingRowIndex]
    );

    const onSelectVatRateItem = useCallback(
        (activeListItemIndex: number) => {
            const selectedVatDataItem =
                accountingVatStore.selectableVatCodeData(accountingRowIndex)[activeListItemIndex];

            updateAccountingWithSelectedVatCode(selectedVatDataItem);
        },
        [updateAccountingWithSelectedVatCode, accountingVatStore, accountingRowIndex]
    );

    const onTabFromVatCodeField = useCallback(() => {
        if (accountingVatStore.selectableVatCodeData(accountingRowIndex).length === 1) {
            const onlyVatRateItemInList = accountingVatStore.selectableVatCodeData(accountingRowIndex)[0];
            updateAccountingWithSelectedVatCode(onlyVatRateItemInList);
        }

        accountingVatStore.setTriggerFocusOnVatRateInput(true, accountingRowIndex);
    }, [accountingVatStore, accountingRowIndex, updateAccountingWithSelectedVatCode]);

    const { onKeyDownFunction, setActiveListItemIndex, NO_LIST_ITEM_SELECTED } = useSelectionListKeyHandler({
        listName: "accounting-vatcode",
        totalNumberOfDisplayedItems: invoiceStore.numberOfDisplayedItems,
        setIsListDisplayed: setDisplayVatCodeList,
        isListDisplayed: displayVatCodeList,
        onSelectItem: onSelectVatRateItem,
        resetSearchQuery: resetSearchQuery,
        additionalTabActionWithNoItemSelected: onTabFromVatCodeField
    });

    const resetAmountOfItemsDisplayedInVatCodeList = useCallback(() => {
        setLastSrollYPositionVatCodeItems(0);
        invoiceStore.setNumberOfDisplayedItems(invoiceStore.sliceSizeOfDisplayedItems);
    }, [invoiceStore]);

    const onChangeVatCodeInput = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            resetAmountOfItemsDisplayedInVatCodeList();

            const inputAccount = e.target.value;
            accountingVatStore.setCurrentVatCodeSearchQuery(inputAccount, accountingRowIndex);
        },
        [accountingVatStore, resetAmountOfItemsDisplayedInVatCodeList, accountingRowIndex]
    );

    const onFocusVatCodeInput = useCallback(
        (e: React.FocusEvent<HTMLInputElement>) => {
            if (
                invoiceStore.currentInvoice?.isAccountingVatCodeSelected(accountingRowIndex) &&
                vatCodeInputRef.current
            ) {
                vatCodeInputRef.current.select();
            }

            resetAmountOfItemsDisplayedInVatCodeList();
            setDisplayVatCodeList(true);
            setActiveListItemIndex(NO_LIST_ITEM_SELECTED);
        },
        [
            resetAmountOfItemsDisplayedInVatCodeList,
            setActiveListItemIndex,
            NO_LIST_ITEM_SELECTED,
            invoiceStore.currentInvoice,
            accountingRowIndex
        ]
    );

    const onClickVatCodeItem = useCallback(
        (vatData: IVatData) => async (e: any) => {
            setActiveListItemIndex(NO_LIST_ITEM_SELECTED);
            setDisplayVatCodeList(false);

            updateAccountingWithSelectedVatCode(vatData);
        },
        [setActiveListItemIndex, NO_LIST_ITEM_SELECTED, updateAccountingWithSelectedVatCode]
    );

    const onScrollVatCodeList = useCallback(
        (e: React.BaseSyntheticEvent) => {
            const windowHeight = e.target.clientHeight;
            const pixelsFromTop = e.target.scrollTop;
            const totalHeight = e.target.scrollHeight;

            const is150PixelsFromBottom = totalHeight - pixelsFromTop < windowHeight + 150;
            const isScrollingDown = pixelsFromTop > lastScrollPositionYVatCodeItems;

            const hasMoreResulsToDisplay =
                invoiceStore.numberOfDisplayedItems <
                accountingVatStore.selectableVatCodeData(accountingRowIndex).length;

            if (is150PixelsFromBottom && isScrollingDown && hasMoreResulsToDisplay) {
                invoiceStore.setNumberOfDisplayedItems(
                    invoiceStore.numberOfDisplayedItems + invoiceStore.sliceSizeOfDisplayedItems
                );
            }

            setLastSrollYPositionVatCodeItems(pixelsFromTop);
        },
        [lastScrollPositionYVatCodeItems, invoiceStore, accountingVatStore, accountingRowIndex]
    );

    const vatCodeList = (
        <ul
            id="accounting-vatcode-dropdown-list"
            className={`dropdown-list accounting-vatcode krediflow ${
                authStore.user?.erpType === ErpType.IT2 ? "IT2" : "RIMO"
            }`}
            onScroll={onScrollVatCodeList}
        >
            {accountingVatStore.loadingVatDataState === LOADING_VAT_DATA_STATE.LOADING ? (
                <li className="d-flex align-items-center justify-content-center list-item accounting-vatcode loading">
                    <div>
                        <UI.RotatingSpinner noLogo size={20} />
                    </div>
                </li>
            ) : (
                accountingVatStore
                    .selectableVatCodeData(accountingRowIndex)
                    .slice(0, invoiceStore.numberOfDisplayedItems)
                    .map((vatData, index) => {
                        const vatCodeName = accountingVatStore.getFormattedVatCodeFieldValue(vatData);

                        const currentVatCodeSearchQuery =
                            accountingVatStore.getCurrentVatCodeSearchQuery(accountingRowIndex);

                        const formattedVatCodeName = boldenMatchedSubstring(
                            currentVatCodeSearchQuery ?? "",
                            vatCodeName,
                            "highlighted-text accounting-vatcode"
                        );

                        return (
                            <li
                                key={index}
                                className="d-flex align-items-center list-item accounting-vatcode"
                                onClick={onClickVatCodeItem(vatData)}
                                id={`accounting-vatcode-list-item-${index}`}
                            >
                                <div
                                    className={`accounting-vatcode-text ${styles.AccountingAccountText}`}
                                    dangerouslySetInnerHTML={{
                                        __html: formattedVatCodeName
                                    }}
                                />
                            </li>
                        );
                    })
            )}
        </ul>
    );

    return (
        <>
            <UI.Input
                ref={vatCodeInputRef}
                autoComplete="off"
                label={t("screens.kredi_flow.accountings.vat_code").toString()}
                type="text"
                value={accountingVatStore.getCurrentVatCodeSearchQuery(accountingRowIndex) ?? ""}
                onChange={onChangeVatCodeInput}
                onFocus={onFocusVatCodeInput}
                onKeyDown={onKeyDownFunction}
                disabled={!invoiceStore.currentInvoice?.canEditAccountingVatCodeField(accountingRowIndex)}
            />
            {displayVatCodeList ? vatCodeList : undefined}
        </>
    );
};

export const VatCodeField = Sentry.withProfiler(observer(VatCodeFieldBase));
